import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { api } from "../lib/api";
import { ArrowLeft, ArrowRight, BookOpenText, Clock } from "lucide-react";

export default function MendelLessons() {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const lang = i18n.language || "en";
  const [lessons, setLessons] = useState([]);
  const [current, setCurrent] = useState(null);

  useEffect(() => {
    api.get(`/mendel/lessons?lang=${lang}`).then(({ data }) => setLessons(data || []));
  }, [lang]);
  useEffect(() => {
    if (slug) api.get(`/mendel/lessons/${slug}?lang=${lang}`).then(({ data }) => setCurrent(data));
    else setCurrent(null);
  }, [slug, lang]);

  if (!slug) {
    return (
      <div className="container-narrow py-10 md:py-14" data-testid="mendel-lessons-index">
        <Link to="/mendel" className="inline-flex items-center gap-1.5 text-ink2 hover:text-ink mb-6 text-sm"><ArrowLeft className="w-4 h-4" /> Back to Mendel</Link>
        <div className="label-eyebrow mb-2 inline-flex items-center gap-2 text-marigold"><BookOpenText className="w-3 h-3" /> Ms. Pradhan · Mendel curriculum</div>
        <h1 className="font-heading text-4xl md:text-5xl font-semibold mb-2">The 20-lesson biology road-trip.</h1>
        <p className="text-ink2 mb-8 max-w-xl">From "what is a gene" to your first computational research project. Ms. Pradhan narrates.</p>
        <ol className="space-y-2" data-testid="mendel-lessons-list">
          {lessons.map((l) => (
            <li key={l.id}>
              <Link to={`/mendel/lessons/${l.slug}`} className="card-soft p-4 flex items-start gap-3 hover:shadow-[4px_4px_0_0_#1A1B41] transition" data-testid={`mendel-lesson-${l.slug}`}>
                <div className="text-marigold font-hand text-2xl leading-none w-8">{String(l.order).padStart(2, "0")}</div>
                <div className="flex-1 min-w-0">
                  <div className="font-heading text-lg font-semibold">{l.title}</div>
                  <div className="text-ink2 text-sm mt-1">{l.summary}</div>
                  <div className="text-[10px] uppercase tracking-widest font-mono text-ink2 mt-2 inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {l.duration} · {l.content ? "Full lesson" : "Coming soon"}</div>
                </div>
                <ArrowRight className="w-4 h-4 text-ink2 shrink-0 mt-2" />
              </Link>
            </li>
          ))}
        </ol>
      </div>
    );
  }

  if (!current) return <div className="container-narrow py-20 text-center text-ink2">Loading lesson…</div>;

  const idx = lessons.findIndex((l) => l.slug === current.slug);
  const prev = idx > 0 ? lessons[idx - 1] : null;
  const next = idx >= 0 && idx < lessons.length - 1 ? lessons[idx + 1] : null;

  return (
    <div className="container-narrow py-10 md:py-14 max-w-3xl" data-testid="mendel-lesson-detail">
      <Link to="/mendel/lessons" className="inline-flex items-center gap-1.5 text-ink2 hover:text-ink mb-6 text-sm"><ArrowLeft className="w-4 h-4" /> All lessons</Link>
      <div className="label-eyebrow mb-2">Lesson {String(current.order).padStart(2, "0")} · {current.duration}</div>
      <h1 className="font-heading text-4xl md:text-5xl font-semibold mb-6">{current.title}</h1>
      {current.content ? (
        <article className="prose prose-slate max-w-none">
          <ReactMarkdown remarkPlugins={[remarkGfm]}>{current.content}</ReactMarkdown>
        </article>
      ) : (
        <div className="card-soft p-6 text-ink2 text-sm">This lesson is coming soon.  In the meantime, jump into the Genome IDE and explore {current.title.toLowerCase()} hands-on.</div>
      )}

      <div className="mt-10 pt-6 border-t border-ink2/20 flex items-center justify-between gap-4">
        {prev ? <Link to={`/mendel/lessons/${prev.slug}`} className="btn-outline-ink text-xs" data-testid="lesson-prev"><ArrowLeft className="w-3 h-3" /> {prev.title}</Link> : <span />}
        {next && <Link to={`/mendel/lessons/${next.slug}`} className="btn-primary text-xs" data-testid="lesson-next">{next.title} <ArrowRight className="w-3 h-3" /></Link>}
      </div>
    </div>
  );
}
